import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const expiration = localStorage.getItem('tokenExpiration');

      if (!token || !expiration) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      if (new Date(expiration) <= new Date()) {
        throw new Error('Token expired');
      }

      const response = await fetch(`${config.endpoints.auth}/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Invalid token');
      }

      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiration');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const login = async (token, expiresIn) => {
    localStorage.setItem('token', token);
    const expirationDate = new Date(Date.now() + expiresIn * 1000).toISOString();
    localStorage.setItem('tokenExpiration', expirationDate);
    setIsAuthenticated(true);
    
    // Set up token refresh before expiration
    const timeUntilExpiry = new Date(expirationDate) - new Date();
    if (timeUntilExpiry > 0) {
      setTimeout(() => {
        checkAuth();
      }, timeUntilExpiry - 60000); // Refresh 1 minute before expiry
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiration');
    setIsAuthenticated(false);
    navigate('/login');
  };

  if (loading) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
